import { Paper, Stack, Typography } from '@mui/material'
import { CarrierType, CarrierVisitStatus } from '@planning/app/api'
import { usePlanningStore } from '@planning/AppProvider'
import { CancelVisitDialog } from '@planning/pages/VesselVisit/Components/molecules/CancelVisitDialog'
import { VisitDataHeaderAttributesCard } from '@planning/pages/VesselVisit/Components/molecules/VisitDataHeaderAttributesCard'
import { VisitTimingDialog } from '@planning/pages/VesselVisit/Components/molecules/VisitTimingDialog'
import { IRailVisitItem } from '@planning/rt-stores/railVisit/RailVisitItem'
import { railVisitService } from '@planning/services'
import { useTranslate } from '@tolgee/react'
import { ButtonWithDropdown, EmptyIndicator, useMinimalsTheme } from '@tom-ui/ui'
import { PATHS } from '@tom-ui/utils'
import dayjs from 'dayjs'
import { observer } from 'mobx-react-lite'
import { FC, useCallback } from 'react'
import { useNavigate } from 'react-router'
import { RailVisitInformation } from './RailVisitInformation'
import { RailVisitTimings } from './RailVisitTimings'

export const RailVisitOverview: FC<{
  railVisitItem: IRailVisitItem
}> = observer(({ railVisitItem }) => {
  const theme = useMinimalsTheme()
  const { t } = useTranslate()
  const navigate = useNavigate()
  const { dialogStore, appViewStore } = usePlanningStore()

  if (!railVisitItem) return <></>

  const railVisit = railVisitItem.data
  const isVisitExpected = railVisit?.status === CarrierVisitStatus.Expected
  const isVisitCancelled = railVisit?.status === CarrierVisitStatus.Cancelled
  const isVisitArrivedButNotDeparted = railVisit?.ata != null && railVisit?.atd == null

  const openCancelVisitDialog = useCallback(() => {
    dialogStore.openDialog(
      <CancelVisitDialog
        identifier={railVisit?.name ?? railVisit?.identifier}
        onSubmit={async () => {
          await railVisitService.cancelVisit({ id: railVisitItem.data.id })
          appViewStore.setShowAlert('success', t('visitCancelled', 'Visit cancelled successfully'))
          dialogStore.close()
        }}
      />,
      false,
      { open: true, maxWidth: 'xs', slotProps: {} },
    )
  }, [dialogStore, railVisit, railVisitItem.data.id, appViewStore, t])

  const openSetAtdModal = useCallback(() => {
    dialogStore.openDialog(
      <VisitTimingDialog
        initialValues={{ date: dayjs(railVisit?.atd ?? railVisit?.etd) }}
        timingType='ATD'
        noCloseAfterConfirm={true}
        onSubmit={async value => {
          const atd = dayjs(value) as any

          await railVisitService.put({
            ...railVisit,
            atd,
            railTrackIds: railVisit.assignedRailTrackIds,
          })
          dialogStore.close()
        }}
      />,
      false,
      { open: true, maxWidth: 'xs', slotProps: {} },
    )
  }, [dialogStore, railVisit])

  const getTimeStampName = (status: CarrierVisitStatus) => {
    switch (status) {
      case CarrierVisitStatus.Departed:
        return 'ATD'
      case CarrierVisitStatus.Completed:
        return 'ATC'
      case CarrierVisitStatus.InOperation:
        return 'ATO'
      case CarrierVisitStatus.Arrived:
        return 'ATA'
      default:
        return ''
    }
  }

  const onRevertStatus = async () => {
    const { status, name, id } = railVisitItem.data ?? {}

    let title = ''
    let description = ''
    let primaryActionText = ''
    let secondaryActionText = ''
    const timeStampName = getTimeStampName(status)

    if (status === CarrierVisitStatus.Closed) {
      title = t('revertToDeparted', 'Revert visit {number} to "Departed"?', { number: name })
      description = t(
        'railVisitDepartedAndWillBeEditable',
        'The rail visit will be set to "Departed" and you will be able to edit it.',
      )
      primaryActionText = t('yesRevert', 'Yes, revert')
      secondaryActionText = t('noKeepStatus', 'No, keep status')
    } else {
      title = t('removeTimeStamp', 'Remove the {name} for the train {trainId}?', {
        name: timeStampName,
        trainId: name,
      })
      description = t(
        'ordersAndStatusWillRemainUnchanged',
        'The orders and their statuses will remain unchanged.',
      )
      primaryActionText = t('yesRemove', 'Yes, remove')
      secondaryActionText = t('noKeepTimeStamp', 'No, keep timestamp')
    }

    const isConfirmed = await appViewStore.setOpenConfirmDialog(
      true,
      <Stack>
        <Typography>{description}</Typography>
      </Stack>,
      title,
      '',
      {
        primaryActionText,
        secondaryActionText,
      },
    )
    if (isConfirmed && id) {
      try {
        await railVisitService.revertCurrentStatus({
          id,
          carrierType: CarrierType.Train,
        })

        if (status === CarrierVisitStatus.Closed) {
          appViewStore.setShowAlert(
            'success',
            t(
              'visitStatusRevertedToDeparted',
              'The visit status was successfully reverted to "Departed".',
            ),
          )
        } else {
          appViewStore.setShowAlert(
            'success',
            t('timestampSuccessFullyRemoved', '{name} was successfully removed.', {
              name: timeStampName,
            }),
          )
        }
      } catch (error: any) {
        appViewStore.setShowAlert('error', t('failedToRemove/Revert', 'Failed to remove/revert.'))
      }
    }
  }

  const getRevertStatusLabel = useCallback(() => {
    let label = ''
    if (railVisit.status === CarrierVisitStatus.Closed) {
      label = t('revertToDeparted', 'Revert to "Departed"')
    } else if (railVisit.status === CarrierVisitStatus.Departed) {
      label = t('removeAtd', 'Remove ATD')
    } else if (railVisit.status === CarrierVisitStatus.Completed) {
      label = t('removeAtc', 'Remove ATC')
    } else if (railVisit.status === CarrierVisitStatus.InOperation) {
      label = t('removeAto', 'Remove ATO')
    } else if (railVisit.status === CarrierVisitStatus.Arrived) {
      label = t('removeAta', 'Remove ATA')
    } else {
      label = ''
    }
    return label
  }, [t, railVisit.status])

  const getVisitOptions = () => {
    const options = []

    options.push({
      label: t('editVisit', 'Edit visit'),
      testId: 'edit-visit-menu-item',
      disabled: isVisitCancelled,
      onClick: () =>
        navigate(`${PATHS.visits.railPlanning.url}/${railVisitItem.id}`, {
          state: { from: 'dashboard' },
        }),
    })

    if (isVisitArrivedButNotDeparted) {
      options.push({
        label: t('departVisit', 'Depart visit'),
        testId: 'depart-visit-menu-item',
        onClick: openSetAtdModal,
      })
    }

    if (isVisitCancelled) {
      options.push({
        label: t('reactivateVisit', 'Reactivate visit'),
        onClick: async () => {
          await railVisitService.reactivateVisit({
            id: railVisitItem.data.id,
          })
          appViewStore.setShowAlert(
            'success',
            t('visitReactivated', 'Visit reactivated successfully'),
          )
        },
      })
    } else {
      options.push({
        label: t('cancelVisit', 'Cancel visit'),
        disabled: !isVisitExpected || railVisitItem.orders.length > 0,
        onClick: openCancelVisitDialog,
        testId: 'cancel-visit-menu-item',
      })
    }

    if (!isVisitExpected && !isVisitCancelled) {
      options.push({
        label: getRevertStatusLabel(),
        onClick: onRevertStatus,
        testId: 'revert-current-status',
      })
    }

    return options
  }

  const isRailcarLengthConflicted =
    railVisit.isInboundRailcarLengthConflicted || railVisit.isOutboundRailcarLengthConflicted

  const trainErrors: string[] = []

  if (railVisit.hasAssignedRailcarsWithoutLength) {
    trainErrors.push(t('missingLength', 'Missing railcar length'))
  }

  if (isRailcarLengthConflicted) {
    trainErrors.push(t('lengthConflict', 'Railcar length conflict'))
  }

  const trainWarningText =
    trainErrors.length > 0
      ? `${trainErrors.join(' & ')} - ${t('editMasterdataToResolve', 'Edit masterdata to resolve conflict')}`
      : undefined

  const trackWarningText = railVisit.isConflicted
    ? t('trackConflictFix', 'Track conflict - Edit visit to resolve conflict')
    : undefined

  return (
    <Paper>
      <Stack
        direction='column'
        sx={{ paddingX: theme.customSpacing.l, paddingY: theme.customSpacing.m }}
      >
        <Stack direction='row' alignItems='center' spacing={theme.customSpacing.xs}>
          <Typography variant='h4'>{t('visitDetails', 'Visit details')}</Typography>
        </Stack>
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={theme.customSpacing.s}
          justifyContent={'space-between'}
        >
          <Stack>
            <RailVisitInformation
              inboundTripId={railVisit.inboundTripIds}
              outboundTripId={railVisit.outboundTripIds}
              operator={railVisit.trainOperator}
              status={railVisit.status}
            />
          </Stack>
          <Stack>
            <Stack direction='column' spacing={theme.customSpacing.s}>
              <Stack
                direction={'row'}
                justifyContent={'space-between'}
                spacing={theme.customSpacing.s}
              >
                <Stack
                  direction={{ xs: 'column', lg: 'row' }}
                  spacing={theme.customSpacing.s}
                  justifyContent={'flex-start'}
                >
                  <VisitDataHeaderAttributesCard
                    title={t('train', 'Train')}
                    width='240px'
                    warningText={trainWarningText}
                    attributes={[
                      {
                        label: t('cargoType', 'Cargo Type'),
                        value: railVisit.cargoType,
                      },
                      {
                        label: t('identifier', 'Identifier'),
                        value: railVisit.identifier ?? railVisit.name,
                      },
                    ]}
                  />
                  <VisitDataHeaderAttributesCard
                    title={t('tracks', 'Tracks')}
                    width='240px'
                    warningText={trackWarningText}
                    attributes={[
                      {
                        label: t('assignedTracks', 'Assigned Tracks'),
                        value: railVisitItem.railTracks?.length
                          ? railVisitItem.railTracks.map(t => t.name).join(', ')
                          : EmptyIndicator,
                      },
                    ]}
                  />
                  <VisitDataHeaderAttributesCard
                    width='275px'
                    title={t('estimates', 'Estimates')}
                    attributes={[
                      {
                        label: t('discharge', 'Discharge'),
                        value: railVisitItem?.data?.dischargeEstimate ?? 0,
                      },
                      {
                        label: t('load', 'Load'),
                        value: railVisitItem?.data?.loadEstimate ?? 0,
                      },
                      {
                        label: t('railCarEstimate', 'Railcars'),
                        value: railVisitItem?.data?.railcarEstimate ?? 0,
                      },
                    ]}
                  />
                </Stack>

                {
                  <Stack direction={'row'}>
                    <ButtonWithDropdown
                      variant='contained'
                      color='inherit'
                      testId='edit-visit-btn'
                      options={getVisitOptions()}
                      label={t('visit', 'Visit')}
                      sx={{ width: '130px !important', height: '100% !important' }}
                    />
                  </Stack>
                }
              </Stack>

              <Stack
                direction={'row'}
                justifyContent={'space-between'}
                spacing={theme.customSpacing.s}
              >
                <RailVisitTimings railVisitItem={railVisitItem} />
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </Paper>
  )
})
